/**
 * Shared enums and types used across features.
 * Auth-specific types → @/types/auth
 * Asset-specific types → @/types/assets
 */

// ====================================================
// User Roles (shared: auth, team, permissions)
// ====================================================
export const UserRole = {
  PLATFORM_ADMIN: 'platform_admin',
  ORG_ADMIN: 'org_admin',
  MANAGER: 'manager',
  TIER3: 'tier3',
  TIER2: 'tier2',
  TIER1: 'tier1',
} as const;

export type UserRole = (typeof UserRole)[keyof typeof UserRole];

// ====================================================
// Permissions (shared: auth, sidebar, permissions)
// ====================================================
export const Permission = {
  MANAGE_OWN_ORGANIZATION: 'manage_own_organization',
  VIEW_OWN_ORGANIZATION: 'view_own_organization',
  APPROVE_ORGANIZATIONS: 'approve_organizations',
  SUSPEND_ORGANIZATIONS: 'suspend_organizations',
  MANAGE_OWN_ORG_USERS: 'manage_own_org_users',
  MANAGE_PLATFORM_USERS: 'manage_platform_users',
  VIEW_OWN_ORG_USERS: 'view_own_org_users',
  VIEW_PLATFORM_USERS: 'view_platform_users',
  ASSIGN_ROLES_OWN_ORG: 'assign_roles_own_org',
  ASSIGN_PLATFORM_ROLES: 'assign_platform_roles',
  CREATE_ASSET: 'create_asset',
  EDIT_ASSET: 'edit_asset',
  DELETE_ASSET: 'delete_asset',
  VIEW_ASSETS: 'view_assets',
  IP_LOOKUP: 'ip_lookup',
  VIEW_VULNERABILITIES: 'view_vulnerabilities',
} as const;

export type Permission = (typeof Permission)[keyof typeof Permission];

// ====================================================
// Organization (shared: org store, platform admin)
// ====================================================
export const OrganizationStatus = {
  PENDING: 'pending',
  APPROVED: 'approved',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  REJECTED: 'rejected',
} as const;

export type OrganizationStatus =
  (typeof OrganizationStatus)[keyof typeof OrganizationStatus];

export const OrganizationType = {
  ENTERPRISE: 'enterprise',
  GOVERNMENT: 'government',
  EDUCATION: 'education',
  NONPROFIT: 'nonprofit',
  HEALTHCARE: 'healthcare',
  FINANCIAL: 'financial',
  MANUFACTURING: 'manufacturing',
  RETAIL: 'retail',
  TECHNOLOGY: 'technology',
  OTHER: 'other',
} as const;

export type OrganizationType =
  (typeof OrganizationType)[keyof typeof OrganizationType];

export interface Organization {
  id: string;
  name: string;
  type: OrganizationType;
  status: OrganizationStatus;
  sector?: string;
  domain?: string;
  enrollSecret?: string;
  createdAt: string;
  updatedAt: string;
  approvedBy?: number | null;
  approvedAt?: string | null;
}

// ====================================================
// Pagination (shared: assets, software)
// ====================================================
export interface PaginationData {
  page: number;
  pageSize: number;
  total: number;
}

// ====================================================
// Software (software feature only)
// ====================================================
export type SoftwareStatus = 'active' | 'deprecated' | 'eol' | 'beta';

export interface Software {
  id: string;
  name: string;
  vendor: string;
  version: string;
  product_name: string;
  release_date: string;
  last_updated: string;
  status: SoftwareStatus;
  installed_count?: number;
  license_type?: 'open_source' | 'commercial' | 'freeware' | 'proprietary';
  category?:
    | 'operating_system'
    | 'database'
    | 'web_server'
    | 'security'
    | 'productivity'
    | 'development'
    | 'utility';
}

export interface SoftwareFilters {
  search?: string;
  vendor?: string | 'all';
  status?: SoftwareStatus | 'all';
  category?: Software['category'] | 'all';
}

export interface SoftwareInstallation {
  id: string;
  asset_id: string;
  asset_name: string;
  asset_type: string;
  location: string;
  installed_date: string;
  installed_by: string;
  status: 'active' | 'inactive';
}

export interface SoftwareVulnerability {
  id: string;
  cve_id: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  cvss_score: number;
  description: string;
  published_date: string;
  patch_available: boolean;
  affected_versions: string[];
}

export interface VulnerabilitySummary {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface SoftwareDetailData extends Software {
  description: string;
  publisher_url: string;
  support_url: string;
  eol_date: string | null;
  latest_version: string;
  vulnerability_count: VulnerabilitySummary;
  installations: SoftwareInstallation[];
  vulnerabilities: SoftwareVulnerability[];
  dependencies: string[];
  system_requirements: {
    min_memory: string;
    min_storage: string;
    supported_os: string[];
  };
}
